import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Storage as SteelIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const SteelFormatReport = () => {
  const [filters, setFilters] = useState({
    steel_code: '',
    specification: '',
    format_type: 'chemical',
    sort_by: 'steel_code'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['steelFormatReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/steel-format?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching steel format report: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = async () => {
    const tableElement = document.getElementById('steel-format-table');
    if (!tableElement) {
      enqueueSnackbar('No report data to print', { variant: 'warning' });
      return;
    }
    try {
      enqueueSnackbar('Generating PDF...', { variant: 'info' });
      const canvas = await html2canvas(tableElement, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;

      // Title
      pdf.setFontSize(16);
      pdf.text('Steel Chemical Composition Format', margin, 15);
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, 22);
      pdf.text('KALYANI FORGE LTD - Materials Quality Control System', margin, 28);

      // Table image
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let yPos = 34;

      if (imgHeight > pageHeight - yPos - margin) {
        // Scale down to fit
        const scale = (pageHeight - yPos - margin) / imgHeight;
        pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth * scale, imgHeight * scale);
      } else {
        pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, imgHeight);
      }

      pdf.save('Steel_Format_Report.pdf');
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error generating PDF', { variant: 'error' });
    }
  };

  const handleDownload = () => {
    if (mockData.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }
    const headers = ['Steel Code', 'Steel Name', 'Specification', 'Carbon Min', 'Carbon Max', 'Manganese Min', 'Manganese Max', 'Silicon Min', 'Silicon Max', 'Chromium Min', 'Chromium Max', 'Nickel Min', 'Nickel Max', 'Molybdenum Min', 'Molybdenum Max'];
    const rows = mockData.map(r => [
      r.steel_code, r.steel_name, r.specification,
      r.carbon_min ?? '', r.carbon_max ?? '',
      r.manganese_min ?? '', r.manganese_max ?? '',
      r.silicon_min ?? '', r.silicon_max ?? '',
      r.chromium_min ?? '', r.chromium_max ?? '',
      r.nickel_min ?? '', r.nickel_max ?? '',
      r.molybdenum_min ?? '', r.molybdenum_max ?? ''
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Steel_Format_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    enqueueSnackbar('Excel (CSV) exported successfully', { variant: 'success' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading steel format report: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockData = [
    {
      steel_code: 'EN24',
      steel_name: 'NICKEL CHROME MOLYBDENUM STEEL',
      specification: 'IS:1570',
      carbon_min: 0.30, carbon_max: 0.38,
      manganese_min: 0.45, manganese_max: 0.70,
      silicon_min: 0.10, silicon_max: 0.35,
      chromium_min: 1.00, chromium_max: 1.40,
      nickel_min: 1.30, nickel_max: 1.70,
      molybdenum_min: 0.20, molybdenum_max: 0.35
    },
    {
      steel_code: 'EN8',
      steel_name: 'MEDIUM CARBON STEEL',
      specification: 'IS:2062',
      carbon_min: 0.36, carbon_max: 0.44,
      manganese_min: 0.60, manganese_max: 0.90,
      silicon_min: 0.10, silicon_max: 0.40,
      chromium_min: null, chromium_max: null,
      nickel_min: null, nickel_max: null,
      molybdenum_min: null, molybdenum_max: null
    },
    {
      steel_code: '20MnCr5',
      steel_name: 'MANGANESE CHROMIUM STEEL',
      specification: 'DIN:17210',
      carbon_min: 0.17, carbon_max: 0.23,
      manganese_min: 1.10, manganese_max: 1.40,
      silicon_min: 0.15, silicon_max: 0.40,
      chromium_min: 1.00, chromium_max: 1.30,
      nickel_min: null, nickel_max: null,
      molybdenum_min: null, molybdenum_max: null
    },
    {
      steel_code: '4140',
      steel_name: 'CHROMIUM MOLYBDENUM STEEL',
      specification: 'AISI:4140',
      carbon_min: 0.38, carbon_max: 0.43,
      manganese_min: 0.75, manganese_max: 1.00,
      silicon_min: 0.15, silicon_max: 0.35,
      chromium_min: 0.80, chromium_max: 1.10,
      nickel_min: null, nickel_max: null,
      molybdenum_min: 0.15, molybdenum_max: 0.25
    }
  ];

  const formatChemicalRange = (min, max) => {
    if (min === null && max === null) return '-';
    if (min === null) return `≤ ${max}`;
    if (max === null) return `≥ ${min}`;
    return `${min} - ${max}`;
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SteelIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Steel Format Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              placeholder="e.g., EN24, EN8"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Specification"
              placeholder="e.g., IS:1570, DIN"
              value={filters.specification}
              onChange={(e) => setFilters({ ...filters, specification: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Format Type</InputLabel>
              <Select
                value={filters.format_type}
                label="Format Type"
                onChange={(e) => setFilters({ ...filters, format_type: e.target.value })}
              >
                <MenuItem value="chemical">Chemical Composition</MenuItem>
                <MenuItem value="mechanical">Mechanical Properties</MenuItem>
                <MenuItem value="complete">Complete Format</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sort_by}
                label="Sort By"
                onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
              >
                <MenuItem value="steel_code">Steel Code</MenuItem>
                <MenuItem value="specification">Specification</MenuItem>
                <MenuItem value="steel_name">Steel Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {mockData.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Steel Grades
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="secondary.main">
                    {new Set(mockData.map(item => item.specification)).size}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Specifications
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {mockData.filter(item => item.chromium_min !== null).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Alloy Steels
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {mockData.filter(item => item.chromium_min === null).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Carbon Steels
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Steel Format Table */}
          <Paper id="steel-format-table">
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Steel Chemical Composition Format
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Standard chemical composition limits for steel grades
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Steel Code</strong></TableCell>
                    <TableCell><strong>Steel Name</strong></TableCell>
                    <TableCell><strong>Specification</strong></TableCell>
                    <TableCell align="center"><strong>Carbon %</strong></TableCell>
                    <TableCell align="center"><strong>Manganese %</strong></TableCell>
                    <TableCell align="center"><strong>Silicon %</strong></TableCell>
                    <TableCell align="center"><strong>Chromium %</strong></TableCell>
                    <TableCell align="center"><strong>Nickel %</strong></TableCell>
                    <TableCell align="center"><strong>Molybdenum %</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No steel format data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={record.steel_code} 
                            color="primary" 
                            variant="outlined" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {record.steel_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={record.specification} 
                            color="secondary" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatChemicalRange(record.carbon_min, record.carbon_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatChemicalRange(record.manganese_min, record.manganese_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatChemicalRange(record.silicon_min, record.silicon_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontFamily="monospace"
                            color={record.chromium_min ? "primary.main" : "text.secondary"}
                          >
                            {formatChemicalRange(record.chromium_min, record.chromium_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontFamily="monospace"
                            color={record.nickel_min ? "secondary.main" : "text.secondary"}
                          >
                            {formatChemicalRange(record.nickel_min, record.nickel_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontFamily="monospace"
                            color={record.molybdenum_min ? "success.main" : "text.secondary"}
                          >
                            {formatChemicalRange(record.molybdenum_min, record.molybdenum_max)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Format Legend */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Chemical Composition Legend
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>C:</strong> Carbon - Provides hardness and strength
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>Mn:</strong> Manganese - Improves hardenability
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>Si:</strong> Silicon - Deoxidizer, improves strength
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>Cr:</strong> Chromium - Increases corrosion resistance
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>Ni:</strong> Nickel - Improves toughness
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  <strong>Mo:</strong> Molybdenum - Increases high-temp strength
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default SteelFormatReport;